import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';

@Injectable({
    providedIn: 'root'
})
export class SEOService {
    constructor(
        private title: Title,
        private meta: Meta,
        @Inject(DOCUMENT) private document: Document
    ) {}

    /**
     * Inject a <link rel="preload" as="image"> for the LCP image.
     * Replaces any prior LCP preload so only one preload exists per page.
     * Safe in SSR (uses DOCUMENT token).
     */
    updateLcpPreload(href: string, imagesrcset?: string, sizes?: string): void {
        if (!href) return;
        const head = this.document.head;
        const existing = head.querySelector('link[data-lcp-preload="true"]');
        if (existing) existing.remove();
        const link = this.document.createElement('link');
        link.setAttribute('rel', 'preload');
        link.setAttribute('as', 'image');
        link.setAttribute('href', href);
        link.setAttribute('fetchpriority', 'high');
        link.setAttribute('data-lcp-preload', 'true');
        if (imagesrcset) link.setAttribute('imagesrcset', imagesrcset);
        if (sizes) link.setAttribute('imagesizes', sizes);
        head.appendChild(link);
    }

    updateTitle(title: string) {
        if (title != null) {
            this.title.setTitle(title);
        }
    }

    updateDescription(desc: string) {
        if (desc != null) {
            this.meta.updateTag({ name: 'description', content: desc });
        }
    }

    updateKeywords(keywords: string) {
        if (keywords != null) {
            this.meta.updateTag({ name: 'keywords', content: keywords });
        }
    }

    // Open Graph
    updateOgTitle(title: string) {
        if (title != null) {
            this.meta.updateTag({ property: 'og:title', content: title });
        }
    }

    updateOgType(type: string) {
        if (type != null) {
            this.meta.updateTag({ property: 'og:type', content: type });
        }
    }

    updateOgDescription(desc: string) {
        if (desc != null) {
            this.meta.updateTag({ property: 'og:description', content: desc });
        }
    }

    updateOgImage(image: string) {
        if (image != null) {
            this.meta.updateTag({ property: 'og:image', content: image });
        }
    }

    updateOgUrl(url: string) {
        if (url != null) {
            this.meta.updateTag({ property: 'og:url', content: url });
        }
    }

    // Twitter
    updateTwitterCard(card: string) {
        if (card != null) {
            this.meta.updateTag({ name: 'twitter:card', content: card });
        }
    }

    updateTwitterTitle(title: string) {
        if (title != null) {
            this.meta.updateTag({ name: 'twitter:title', content: title });
        }
    }

    updateTwitterDescription(desc: string) {
        if (desc != null) {
            this.meta.updateTag({ name: 'twitter:description', content: desc });
        }
    }

    updateTwitterImage(image: string) {
        if (image != null) {
            this.meta.updateTag({ name: 'twitter:image', content: image });
        }
    }

    // Canonical
    updateCanonical(url: string) {
        if (url != null) {
            this.meta.updateTag({ rel: 'canonical', content: url });
        }
    }
}
